/* yl-filter-bar.js — one-row filter bar for collection + search listings.
 *
 * Markup: snippets/yl-facets.liquid (layout 'bar') + snippets/yl-sort-menu.liquid (sheet: true).
 * Styles: assets/yl-emerald-filters.css (.yl-fbar*).
 *
 * Progressive enhancement only — without this file every chip is a native
 * <details>/<summary> and each panel's Apply button submits the GET form.
 * With it:
 *   - one panel open at a time; click outside / Esc / close button / scrim closes
 *     and returns focus to the chip's <summary>;
 *   - list filters submit on change (short debounce so several quick ticks
 *     become one request), so their Apply buttons are hidden (price keeps Apply);
 *   - < 768px panels are bottom sheets: page scroll is locked and Tab stays inside;
 *   - the horizontally scrolling chip rail shows edge fades while more chips exist.
 *
 * The sort <details> also carries [data-yl-fbar-item]; yololife-ui-controls.js
 * independently closes it on outside click / Esc — both handlers are idempotent.
 */
(function () {
  'use strict';

  const bars = Array.from(document.querySelectorAll('[data-yl-fbar]'));
  if (!bars.length) return;

  const ITEM = 'details[data-yl-fbar-item]';
  const OPEN_ITEM = '[data-yl-fbar] ' + ITEM + '[open]';
  const SHEET_LOCK_CLASS = 'yl-fbar-sheet-open';
  const SUBMIT_DELAY = 350;
  const mobile = window.matchMedia('(max-width: 767px)');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  let submitTimer = 0;

  const openItems = () => Array.from(document.querySelectorAll(OPEN_ITEM));

  const syncSheetLock = () => {
    document.documentElement.classList.toggle(SHEET_LOCK_CLASS, mobile.matches && openItems().length > 0);
  };

  const closeItem = (details, focusSummary) => {
    if (!details || !details.open) return;
    details.open = false;
    if (focusSummary) {
      const summary = details.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
    }
  };

  const panelOf = (details) => details.querySelector('.yl-fbar__panel');

  // Desktop: keep the dropdown inside the viewport when its chip sits near the right edge.
  const alignPanel = (details) => {
    const panel = panelOf(details);
    if (!panel) return;
    panel.classList.remove('is-align-end');
    if (mobile.matches) return;
    const rect = panel.getBoundingClientRect();
    if (rect.right > document.documentElement.clientWidth - 12) panel.classList.add('is-align-end');
  };

  const markResultsLoading = () => {
    document.querySelectorAll('[data-yl-collection-results]').forEach((results) => {
      results.classList.add('is-loading');
      results.setAttribute('aria-busy', 'true');
    });
  };

  // Empty price bounds add nothing to the query; skip them so URLs stay clean.
  // They are re-enabled on pageshow because a bfcache restore keeps the disabled state.
  const pruneEmptyFields = (form) => {
    form.querySelectorAll('input[type="number"]').forEach((field) => {
      if (field.value.trim() === '') {
        field.disabled = true;
        field.dataset.ylFbarPruned = 'true';
      }
    });
  };

  const submitForm = (form) => {
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      pruneEmptyFields(form);
      markResultsLoading();
      form.submit();
    }
  };

  // --- Rail edge fades (mobile horizontal scroll) ---------------------------
  const updateRail = (rail) => {
    const scroller = rail.querySelector('[data-yl-fbar-scroller]');
    if (!scroller) return;
    const max = scroller.scrollWidth - scroller.clientWidth;
    rail.classList.toggle('has-more-start', max > 4 && scroller.scrollLeft > 4);
    rail.classList.toggle('has-more-end', max > 4 && scroller.scrollLeft < max - 4);
  };

  const bindRail = (rail) => {
    const scroller = rail.querySelector('[data-yl-fbar-scroller]');
    if (!scroller) return;
    let queued = false;
    scroller.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        updateRail(rail);
      });
    }, { passive: true });
    updateRail(rail);
  };

  bars.forEach((bar) => {
    bar.classList.add('is-enhanced');
    bar.querySelectorAll('[data-yl-fbar-rail]').forEach(bindRail);
  });

  // --- Open / close ---------------------------------------------------------
  // `toggle` does not bubble, so listen in the capture phase.
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLElement) || !details.matches(ITEM)) return;
    if (details.open) {
      openItems().forEach((other) => {
        if (other !== details) other.open = false;
      });
      alignPanel(details);
      if (mobile.matches) {
        const close = details.querySelector('.yl-fbar__close');
        if (close) close.focus({ preventScroll: true });
      }
    }
    syncSheetLock();
  }, true);

  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-yl-fbar-close]');
    if (closer) {
      const details = closer.closest(ITEM);
      if (details) {
        event.preventDefault();
        closeItem(details, true);
      }
      return;
    }
    const clickedFocusable = Boolean(event.target.closest(FOCUSABLE + ', summary'));
    openItems().forEach((details) => {
      if (details.contains(event.target)) return;
      // Hand focus back to the chip unless the click itself moved focus somewhere useful.
      closeItem(details, !clickedFocusable);
    });
  });

  document.addEventListener('keydown', (event) => {
    const open = openItems()[0];
    if (!open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeItem(open, true);
      return;
    }

    // Bottom sheet behaves as a modal surface: keep Tab inside the open panel.
    if (event.key === 'Tab' && mobile.matches) {
      const panel = panelOf(open);
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // --- Auto-submit list filters ---------------------------------------------
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;
    if (!input.closest('[data-yl-fbar-form]')) return;
    const form = input.form;
    window.clearTimeout(submitTimer);
    submitTimer = window.setTimeout(() => submitForm(form), SUBMIT_DELAY);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-yl-fbar]')) return;
    window.clearTimeout(submitTimer);
    if (form.matches('[data-yl-fbar-form]')) pruneEmptyFields(form);
    markResultsLoading();
  });

  // --- Viewport / history housekeeping --------------------------------------
  const onViewportChange = () => {
    openItems().forEach(alignPanel);
    syncSheetLock();
  };
  if (typeof mobile.addEventListener === 'function') {
    mobile.addEventListener('change', onViewportChange);
  } else if (typeof mobile.addListener === 'function') {
    mobile.addListener(onViewportChange);
  }

  let resizeQueued = false;
  window.addEventListener('resize', () => {
    if (resizeQueued) return;
    resizeQueued = true;
    window.requestAnimationFrame(() => {
      resizeQueued = false;
      document.querySelectorAll('[data-yl-fbar-rail]').forEach(updateRail);
    });
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-yl-fbar-pruned]').forEach((field) => {
      field.disabled = false;
      delete field.dataset.ylFbarPruned;
    });
    openItems().forEach((details) => { details.open = false; });
    syncSheetLock();
  });
})();
